import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

const Disclaimer = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-gray-50 py-12"
    >
      <Helmet>
        <title>Legal Disclaimer - Paradise-Performance</title>
        <meta name="description" content="Legal disclaimer for our website and services. Important information about liability and usage terms." />
              <link rel="canonical" href="https://paradise-performance.com/disclaimer" />
      </Helmet>

      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Legal Disclaimer</h1>
          
          <div className="prose prose-gray max-w-none">
            <p className="text-sm text-gray-600 mb-6">Last Updated: January 1, 2024</p>
            
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Website Content Disclaimer</h2>
              <p className="text-gray-700 mb-4">
                The information contained on the Paradise-Performance website is for general information purposes only. While we strive to keep the information up-to-date and accurate, we make no representations or warranties of any kind, express or implied, about the completeness, accuracy, reliability, suitability, or availability of the website or the information, products, services, or related graphics contained on the website.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Vehicle Information Accuracy</h2>
              <p className="text-gray-700 mb-4">
                All vehicle specifications, features, and prices are subject to change without notice. While we make every effort to ensure accuracy:
              </p>
              <ul className="list-disc ml-6 mb-4 text-gray-700">
                <li>Vehicle images may not represent the exact vehicle in stock</li>
                <li>Options and features listed may vary from actual inventory</li>
                <li>Mileage figures are EPA estimates and actual mileage may vary</li>
                <li>Prices exclude government fees, taxes, finance charges, and dealer fees</li>
                <li>All vehicles subject to prior sale</li>
              </ul>
              <p className="text-gray-700 mb-4">
                We recommend contacting us directly to verify vehicle availability, specifications, and pricing before making any decisions based on website information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">No Professional Advice</h2>
              <p className="text-gray-700 mb-4">
                The content on this website does not constitute professional automotive, financial, legal, or tax advice. Always seek the advice of qualified professionals for specific questions regarding:
              </p>
              <ul className="list-disc ml-6 mb-4 text-gray-700">
                <li>Vehicle financing and credit decisions</li>
                <li>Insurance requirements and coverage</li>
                <li>Tax implications of vehicle purchases</li>
                <li>Legal matters related to vehicle transactions</li>
                <li>Vehicle safety and maintenance</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Used Vehicle Disclaimer</h2>
              <p className="text-gray-700 mb-4">
                <strong>IMPORTANT:</strong> Unless otherwise stated in writing, all used vehicles are sold "AS IS" with no warranty, either expressed or implied, including the implied warranties of merchantability and fitness for a particular purpose.
              </p>
              <p className="text-gray-700 mb-4">
                The buyer accepts full responsibility for all repairs and maintenance after the sale. We strongly recommend having any used vehicle inspected by an independent mechanic before purchase.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Finance Calculator Disclaimer</h2>
              <p className="text-gray-700 mb-4">
                Payment calculations provided on this website are estimates for informational purposes only and do not constitute an offer of credit. Actual terms may vary based on:
              </p>
              <ul className="list-disc ml-6 mb-4 text-gray-700">
                <li>Credit approval and creditworthiness</li>
                <li>Down payment amount</li>
                <li>Trade-in value</li>
                <li>Actual interest rates</li>
                <li>Loan term and conditions</li>
                <li>Taxes, fees, and other charges</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">External Links Disclaimer</h2>
              <p className="text-gray-700 mb-4">
                Our website may contain links to external websites that are not provided or maintained by Paradise-Performance. We do not guarantee the accuracy, relevance, timeliness, or completeness of any information on these external websites.
              </p>
              <p className="text-gray-700 mb-4">
                The inclusion of any link does not imply endorsement by Paradise-Performance. Use of any external website is at your own risk.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Limitation of Liability</h2>
              <p className="text-gray-700 mb-4">
                To the maximum extent permitted by applicable law, Paradise-Performance shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from:
              </p>
              <ul className="list-disc ml-6 mb-4 text-gray-700">
                <li>Your use or inability to use our website or services</li>
                <li>Any unauthorized access to or use of our servers</li>
                <li>Any interruption or cessation of transmission to or from our website</li>
                <li>Any bugs, viruses, or other harmful code transmitted through our website</li>
                <li>Any errors or omissions in any content</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Indemnification</h2>
              <p className="text-gray-700 mb-4">
                You agree to defend, indemnify, and hold harmless Paradise-Performance, its officers, directors, employees, agents, and affiliates from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees arising out of or relating to your violation of these terms or your use of the website.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Texas Law</h2>
              <p className="text-gray-700 mb-4">
                This disclaimer is governed by the laws of the State of Texas. Any disputes relating to this disclaimer shall be subject to the exclusive jurisdiction of the courts of Wise County, Texas.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">FTC Used Car Rule</h2>
              <p className="text-gray-700 mb-4">
                Paradise-Performance complies with the Federal Trade Commission's Used Car Rule. Buyers Guides are posted on all used vehicles as required by federal law. The Buyers Guide supersedes any contrary provisions in the contract of sale.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Modifications</h2>
              <p className="text-gray-700 mb-4">
                Paradise-Performance reserves the right to modify this disclaimer at any time without prior notice. Your continued use of the website following any changes constitutes acceptance of the modified disclaimer.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Information</h2>
              <p className="text-gray-700 mb-4">
                For questions about this disclaimer, please contact:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg text-gray-700">
                <p className="font-semibold">Paradise-Performance</p>
                <p>305 Co Rd 3470</p>
                <p>Paradise, TX 76073</p>
                <p>Phone: (817) 382-2316</p>
                <p>Email: service@paradise-performance.com</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default Disclaimer;
